import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import Certificate from "@/components/Certificate";
import { color } from "@/lib/theme";

// Node tones, by the role the node plays in the diagram — the map exists only
// so a long data table stays readable. Keys are tone names, never hues.
const C = { brand: "brand", deep: "brand-deep", gold: "gold", valid: "valid", warn: "warn", danger: "danger" };
const ease = [0.16, 1, 0.3, 1];
const vp = { once: true, amount: 0.3 };

function Dot({ tone = C.brand, size = 8, glow = true }) {
  return <span className="inline-block shrink-0 rounded-full" style={{ width: size, height: size, background: color(tone), boxShadow: glow ? `0 0 6px ${color(tone, 0.35)}` : "none" }} />;
}
function DemoTag({ children = "Simulated environment · Demonstration data" }) {
  return <div className="inline-flex items-center gap-2 rounded-full border border-line bg-panel-raised/60 px-2.5 py-1 font-mono text-[8.5px] uppercase tracking-[0.16em] text-content-faint"><span className="h-1 w-1 rounded-full bg-brand/70" />{children}</div>;
}

/* ============================================================= */
/*  HERO — Meira Control Plane (system journey)                  */
/* ============================================================= */
const JOURNEY = [
  ["AI System", "Customer Service Agent", C.brand],
  ["Inventory", "Registered", C.brand],
  ["Security", "Assessment complete", C.brand],
  ["Findings", "2 open", C.warn],
  ["Risk", "Mapped", C.brand],
  ["Controls", "Assigned", C.deep],
  ["Evidence", "Current", C.gold],
  ["Frameworks", "Screened", C.brand],
  ["Assurance", "Governed", C.gold],
  ["Trust Passport", "Current", C.gold],
  ["Verified", "Independently checkable", C.valid],
];

export function ControlPlaneHero() {
  const reduce = useReducedMotion();
  return (
    <div className="relative rounded-xl border border-line bg-panel-raised/70 p-5 shadow-panel sm:p-6" data-testid="control-plane-hero">
      <div className="flex flex-col gap-2.5 border-b border-line pb-4 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-gold" style={{ boxShadow: `0 0 6px ${color("gold", 0.4)}` }} />
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-content">Meira Control Plane</span>
        </div>
        <DemoTag />
      </div>
      <ol className="relative mt-5">
        <span className="absolute left-[9px] top-2 h-[calc(100%-1.5rem)] w-px bg-gradient-to-b from-brand/50 via-line to-valid/50" />
        {JOURNEY.map(([label, state, tone], i) => (
          <motion.li
            key={label}
            initial={reduce ? {} : { opacity: 0, x: 14 }}
            whileInView={reduce ? {} : { opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.15 + i * 0.08, ease }}
            className="relative flex items-center gap-4 pb-3.5 last:pb-0"
          >
            <span className="relative z-10 flex h-[19px] w-[19px] items-center justify-center rounded-full border border-line bg-ink">
              <Dot tone={tone} size={7} />
            </span>
            <div className="flex min-w-0 flex-1 flex-col gap-0.5 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
              <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-content-muted">{label}</span>
              <span className="text-[12px] sm:text-right" style={{ color: color(i === JOURNEY.length - 1 ? C.valid : "content-muted") }}>{state}</span>
            </div>
          </motion.li>
        ))}
      </ol>
    </div>
  );
}

/* ============================================================= */
/*  Assurance lifecycle (connected, loops back)                  */
/* ============================================================= */
const LIFECYCLE = [
  ["Discover", "Register systems, models and agents."],
  ["Test", "Run AI security and behavioral assessments."],
  ["Find", "Identify weaknesses and governance gaps."],
  ["Remediate", "Assign and track corrective actions."],
  ["Govern", "Connect ownership, risks, policies and controls."],
  ["Map", "Screen controls and evidence against frameworks."],
  ["Evidence", "Preserve traceable assessment evidence."],
  ["Assure", "Resolve readiness and assurance state."],
  ["Prove", "Generate Trust Passport and assurance records."],
  ["Monitor / Reassess", "Respond when the AI system or context changes."],
];

export function AssuranceLifecycle() {
  return (
    <div data-testid="assurance-lifecycle">
      <motion.div initial="hidden" whileInView="show" viewport={vp} variants={{ hidden: {}, show: { transition: { staggerChildren: 0.06 } } }} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {LIFECYCLE.map(([name, desc], i) => {
          const last = i === LIFECYCLE.length - 1;
          return (
            <motion.div key={name} variants={{ hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0, transition: { duration: 0.45, ease } } }} className={`relative rounded-lg border p-4 ${last ? "border-gold/25 bg-gold/[0.04]" : "border-line bg-panel-raised/60"}`}>
              <div className="flex items-center gap-2">
                <span className="font-mono text-[10px] text-gold">{String(i + 1).padStart(2, "0")}</span>
                <span className="font-display text-sm font-medium text-content">{name}</span>
              </div>
              <p className="mt-2 text-[12px] leading-snug text-content-muted">{desc}</p>
              {last && <span className="mt-3 inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.14em] text-gold">↺ Loops back to Test</span>}
            </motion.div>
          );
        })}
      </motion.div>
      <p className="mt-6 font-mono text-[11px] tracking-wide text-content-faint">Every stage strengthens the same evidence and trust layer.</p>
    </div>
  );
}

/* ============================================================= */
/*  Evidence & Trust Core (radial + semantic relationships)      */
/* ============================================================= */
const CORE_RING = [
  ["AI Systems", C.brand], ["Models", C.brand], ["Agents", C.brand], ["Assessments", C.brand],
  ["Findings", C.warn], ["Risks", C.danger], ["Controls", C.deep], ["Frameworks", C.brand],
  ["Evidence", C.gold], ["Owners", C.brand], ["Trust Passport", C.gold], ["Assurance Records", C.gold],
].map(([label, tone], i, arr) => {
  const a = (-90 + i * (360 / arr.length)) * (Math.PI / 180);
  return { label, tone, x: 50 + 42 * Math.cos(a), y: 48 + 40 * Math.sin(a) };
});
const CORE = { x: 50, y: 48 };
const RELATIONSHIPS = [
  ["AI System", "assessed by", "Assessment"],
  ["Assessment", "creates", "Finding"],
  ["Finding", "maps to", "Risk"],
  ["Risk", "addressed by", "Control"],
  ["Control", "supported by", "Evidence"],
  ["Evidence", "supports", "Framework requirement"],
  ["Requirement", "contributes to", "Assurance"],
  ["Assurance", "represented in", "Trust Passport"],
  ["Trust Passport", "supports", "Verifiable record"],
];

export function EvidenceCore() {
  const reduce = useReducedMotion();
  return (
    <div className="grid gap-8 lg:grid-cols-[1.3fr_1fr] lg:items-center" data-testid="evidence-core">
      {/* phone/tablet: the ring flattened into a hub with its nodes as chips */}
      <div className="rounded-lg border border-line bg-panel-raised/50 p-5 lg:hidden">
        <div className="flex justify-center">
          <div className="relative flex h-[92px] w-[92px] items-center justify-center rounded-full border border-gold/40 bg-panel-raised/90 text-center">
            {!reduce && <span className="absolute inset-0 animate-pulse-ring rounded-full bg-gold/40" />}
            <span className="relative z-10 px-2 font-mono text-[8.5px] uppercase leading-tight tracking-[0.12em] text-gold">Meira<br />Evidence &amp;<br />Trust Layer</span>
          </div>
        </div>
        <div className="mx-auto my-4 h-5 w-px bg-gradient-to-b from-gold/40 to-line" />
        <div className="flex flex-wrap justify-center gap-1.5">
          {CORE_RING.map((n) => (
            <span key={n.label} className="inline-flex items-center gap-1.5 rounded-full border border-line bg-panel px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.1em] text-content-muted">
              <Dot tone={n.tone} size={5} />{n.label}
            </span>
          ))}
        </div>
      </div>
      <div className="relative mx-auto hidden aspect-square w-full max-w-[520px] lg:block">
        <svg className="absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
          {CORE_RING.map((n, i) => (
            <motion.line key={n.label} x1={CORE.x} y1={CORE.y} x2={n.x} y2={n.y} stroke={color(n.tone === C.gold ? "gold" : "brand", n.tone === C.gold ? 0.4 : 0.26)} strokeWidth={n.tone === C.gold ? 0.7 : 0.5} vectorEffect="non-scaling-stroke" initial={reduce ? {} : { pathLength: 0, opacity: 0 }} whileInView={reduce ? {} : { pathLength: 1, opacity: 1 }} viewport={vp} transition={{ duration: 1, delay: 0.15 + i * 0.05, ease }} />
          ))}
        </svg>
        {/* The centring translate stays on a static wrapper: framer-motion writes an
            inline `transform`, which would override a Tailwind -translate-*-1/2 here. */}
        {CORE_RING.map((n, i) => (
          <div key={n.label} className="absolute -translate-x-1/2 -translate-y-1/2" style={{ left: `${n.x}%`, top: `${n.y}%` }}>
            <motion.span className="flex flex-col items-center gap-1" initial={reduce ? {} : { opacity: 0, scale: 0.5 }} whileInView={reduce ? {} : { opacity: 1, scale: 1 }} viewport={vp} transition={{ duration: 0.5, delay: 0.2 + i * 0.05, ease }}>
              <Dot tone={n.tone} size={n.tone === C.gold ? 9 : 7} /><span className="whitespace-nowrap font-mono text-[9px] uppercase tracking-[0.1em] text-content-muted">{n.label}</span>
            </motion.span>
          </div>
        ))}
        <div className="absolute -translate-x-1/2 -translate-y-1/2" style={{ left: `${CORE.x}%`, top: `${CORE.y}%` }}>
          <motion.div className="relative flex h-[116px] w-[116px] items-center justify-center rounded-full border border-gold/40 bg-panel-raised/90 text-center backdrop-blur-sm" initial={reduce ? {} : { opacity: 0, scale: 0.6 }} whileInView={reduce ? {} : { opacity: 1, scale: 1 }} viewport={vp} transition={{ duration: 0.6, ease }}>
            {!reduce && <span className="absolute inset-0 animate-pulse-ring rounded-full bg-gold/40" />}
            <span className="relative z-10 px-3 font-mono text-[9.5px] uppercase leading-tight tracking-[0.14em] text-gold">Meira<br />Evidence &<br />Trust Layer</span>
          </motion.div>
        </div>
      </div>
      <div>
        <div className="mb-4 font-mono text-[10px] uppercase tracking-[0.2em] text-content-faint">How it connects</div>
        <ul className="flex flex-col gap-2.5">
          {RELATIONSHIPS.map(([a, rel, b]) => (
            <li key={`${a}-${b}`} className="flex flex-wrap items-center gap-2 text-[13px]">
              <span className="text-content">{a}</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-brand-soft">{rel}</span>
              <span className="text-content">{b}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/* ============================================================= */
/*  Capability layers (interactive lineage)                      */
/* ============================================================= */
const LAYERS = [
  ["Discover & understand", ["AI System Inventory", "Trust Graph"]],
  ["Test & remediate", ["AI Security Assessments", "Findings & Remediation"]],
  ["Govern & control", ["Governance Policies", "Risk Register", "Framework Screening"]],
  ["Evidence & accountability", ["Evidence", "Audit Trail"]],
  ["Assure & prove", ["Trust Passport", "Verifiable Assurance Records", "Assurance Dashboard"]],
];
const LINEAGE = {
  "AI Security Assessments": ["Assessment", "Finding", "Risk", "Control", "Evidence", "Assurance"],
  "Framework Screening": ["Requirement", "Control", "Evidence", "Readiness", "Assurance"],
  "Trust Graph": ["AI System", "Finding", "Risk", "Control", "Evidence", "Framework", "Assurance"],
  "Trust Passport": ["AI System", "Security state", "Governance state", "Evidence", "Assurance history", "Certificate"],
  "Findings & Remediation": ["Finding", "Owner", "Remediation", "Retest", "Evidence"],
  "Verifiable Assurance Records": ["Assurance", "Trust Passport", "Record", "Public verification"],
  __default: ["Input", "Evidence", "Assurance"],
};

export function CapabilityLayers() {
  const [active, setActive] = useState("AI Security Assessments");
  const chain = LINEAGE[active] || LINEAGE.__default;
  return (
    <div data-testid="capability-layers">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {LAYERS.map(([title, caps], i) => (
          <div key={title} className="rounded-lg border border-line bg-panel-raised/50 p-4">
            <div className="flex items-center gap-2">
              <span className="font-mono text-[10px] text-gold">L{i + 1}</span>
              <span className="font-mono text-[9.5px] uppercase leading-tight tracking-[0.12em] text-content-faint">{title}</span>
            </div>
            <div className="mt-3 flex flex-col gap-1.5">
              {caps.map((cap) => {
                const on = active === cap;
                return (
                  <button key={cap} data-testid={`cap-${cap.toLowerCase().replace(/[^a-z]+/g, "-")}`} onMouseEnter={() => setActive(cap)} onFocus={() => setActive(cap)} onClick={() => setActive(cap)} className={`min-h-[40px] rounded-md border px-2.5 py-2 text-left text-[12px] transition-colors duration-200 focus:outline-none ${on ? "border-brand/40 bg-brand/10 text-content" : "border-line-soft bg-panel-raised/60 text-content-muted hover:text-content"}`}>
                    {cap}
                  </button>
                );
              })}
            </div>
            <div className="mt-3 font-mono text-[8px] uppercase tracking-[0.12em] text-content-faint">→ writes to evidence layer</div>
          </div>
        ))}
      </div>
      <div className="mt-4 rounded-lg border border-line bg-gradient-to-r from-brand/[0.06] to-gold/[0.06] p-4" data-testid="capability-lineage">
        <div className="mb-3 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.16em] text-gold">
          <Dot tone={C.gold} size={6} /> {active} · lineage
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {chain.map((s, i) => (
            <React.Fragment key={s}>
              <motion.span key={`${active}-${s}`} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: i * 0.05 }} className="rounded-full border border-line bg-panel-raised px-3 py-1 font-mono text-[10px] tracking-wide text-content">{s}</motion.span>
              {i < chain.length - 1 && <span className="text-content-faint">→</span>}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ============================================================= */
/*  Signal → Proof (alternating timeline)                        */
/* ============================================================= */
const SIGNAL = [
  ["AI System", "Customer Service Agent", null, C.brand],
  ["Security Test", "Prompt Injection Assessment", null, C.brand],
  ["Finding", "FND-DEMO-0042 · Indirect prompt-injection exposure", "HIGH", C.warn],
  ["Risk", "RISK-DEMO-0017 · Unauthorized instruction execution", null, C.danger],
  ["Control", "CTRL-DEMO-0021 · Input Validation + Tool Access Boundary", null, C.deep],
  ["Owner", "AI Security", null, C.brand],
  ["Remediation", "Guardrail and permission policy updated", null, C.brand],
  ["Retest", "Original assessment pattern rerun", null, C.brand],
  ["Result", "PASSED", "PASS", C.valid],
  ["Evidence", "EV-DEMO-00481 · Assessment · Remediation · Retest · Timestamp", null, C.gold],
  ["Framework Mapping", "Applicable requirements / controls", null, C.brand],
  ["Assurance", "Current state updated", null, C.gold],
  ["Proof", "Assurance record generated · Trust Passport: Governed", null, C.valid],
];

export function SignalToProof() {
  return (
    <div className="relative mx-auto max-w-3xl" data-testid="signal-to-proof">
      <span className="absolute left-[11px] top-2 h-[calc(100%-1rem)] w-px bg-gradient-to-b from-brand/40 via-line to-valid/50 md:left-1/2 md:-translate-x-1/2" />
      <ol className="flex flex-col gap-3">
        {SIGNAL.map(([label, body, badge, tone], i) => {
          const flip = i % 2 === 1;
          return (
            <motion.li key={label + i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.6 }} transition={{ duration: 0.45, ease }} className={`relative pl-9 md:w-1/2 md:pl-0 ${flip ? "md:ml-auto md:pl-9" : "md:pr-9 md:text-right"}`}>
              <span className={`absolute top-2.5 z-10 flex h-[22px] w-[22px] items-center justify-center rounded-full border border-line bg-ink left-0 ${flip ? "md:-left-[11px]" : "md:left-auto md:-right-[11px]"}`}>
                <Dot tone={tone} size={8} />
              </span>
              <div className="rounded-lg border border-line bg-panel-raised/70 p-4">
                <div className={`flex items-center gap-2 ${flip ? "" : "md:justify-end"}`}>
                  <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-content-faint">{label}</span>
                  {badge && <span className="rounded-full px-2 py-0.5 font-mono text-[8px] uppercase tracking-[0.12em]" style={{ color: color(tone), background: color(tone, 0.09), border: `1px solid ${color(tone, 0.27)}` }}>{badge}</span>}
                </div>
                <div className="mt-1.5 text-[13px] leading-snug text-content">{body}</div>
              </div>
            </motion.li>
          );
        })}
      </ol>
    </div>
  );
}

/* ============================================================= */
/*  Role console (tabs → one interface)                          */
/* ============================================================= */
const ROLES = {
  "CISO": { metrics: [["Open findings", "7", C.warn], ["High severity", "1", C.danger], ["Remediation SLA", "94%", C.valid], ["Systems at risk", "2", C.warn]], list: ["Prompt-injection exposure · remediated", "Data-leakage probe · monitoring", "Tool-abuse boundary · control assigned"], listTitle: "Security exposure" },
  "Chief AI Officer": { metrics: [["AI systems", "24", C.brand], ["Agents", "9", C.brand], ["Owned & governed", "88%", C.valid], ["Trust state", "Governed", C.gold]], list: ["Customer Service Agent · Governed", "Credit Decisioning Model · Certification-ready", "Triage Assistant · Tested"], listTitle: "AI estate" },
  "Governance / Risk": { metrics: [["Open risks", "12", C.warn], ["Controls mapped", "86%", C.valid], ["Frameworks", "4", C.brand], ["Evidence current", "91%", C.valid]], list: ["ISO/IEC 42001 · readiness 78%", "NIST AI RMF · screened", "EU AI Act · screened"], listTitle: "Controls & frameworks" },
  "Audit / Assurance": { metrics: [["Assurance records", "18", C.gold], ["Verifiable", "18", C.valid], ["Evidence freshness", "Current", C.valid], ["Audit events", "1.2k", C.brand]], list: ["CERT-DEMO-000148 · Verified", "Traceable evidence · Assessment → Retest", "Public verification · no login"], listTitle: "Traceability & records" },
};
const ROLE_KEYS = Object.keys(ROLES);

export function RoleConsole() {
  const [role, setRole] = useState(ROLE_KEYS[0]);
  const r = ROLES[role];
  return (
    <div data-testid="role-console">
      <div className="flex flex-wrap gap-2">
        {ROLE_KEYS.map((k) => (
          <button key={k} data-testid={`role-tab-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => setRole(k)} className={`min-h-[38px] rounded-full border px-4 py-2 font-mono text-[10.5px] uppercase tracking-[0.1em] sm:text-[11px] transition-colors duration-200 ${role === k ? "border-brand/40 bg-brand/10 text-brand" : "border-line bg-panel-raised/60 text-content-muted hover:text-content"}`}>{k}</button>
        ))}
      </div>
      <motion.div key={role} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease }} className="mt-5 overflow-hidden rounded-xl border border-line bg-panel-raised/70">
        <div className="flex flex-col gap-2 border-b border-line px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5">
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-content">Meira · {role} view</span>
          <DemoTag>Illustrative Meira interface · Synthetic data</DemoTag>
        </div>
        <div className="grid gap-4 p-4 sm:gap-5 sm:p-5 lg:grid-cols-[1.4fr_1fr]">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {r.metrics.map(([label, val, col]) => (
              <div key={label} className="rounded-lg border border-line bg-panel-raised/60 p-3.5">
                <div className="font-display text-2xl font-light" style={{ color: color(col) }}>{val}</div>
                <div className="mt-1 font-mono text-[8.5px] uppercase leading-tight tracking-[0.1em] text-content-faint">{label}</div>
              </div>
            ))}
          </div>
          <div className="rounded-lg border border-line bg-panel-raised/60 p-4">
            <div className="mb-3 font-mono text-[9px] uppercase tracking-[0.18em] text-content-faint">{r.listTitle}</div>
            <ul className="flex flex-col gap-2.5">
              {r.list.map((item) => (
                <li key={item} className="flex items-start gap-2 text-[12.5px] text-content-muted"><Dot tone={C.deep} size={6} /><span>{item}</span></li>
              ))}
            </ul>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

/* ============================================================= */
/*  Continuous assurance loop + advanced capabilities            */
/* ============================================================= */
const CONT = ["AI system change", "Impact identified", "Affected risks / controls", "Reassessment", "New evidence", "Assurance state updated", "Verification status updated"];
const ADVANCED = [
  ["Regulation-to-Control Mapping", "Preview"],
  ["AI Governance Digital Twin", "Concept"],
  ["Autonomous Red-Team → Fix → Prove", "Planned"],
  ["Agentic AI Governance", "Preview"],
  ["Continuous Assurance", "Pilot"],
];
const MAT_STYLE = {
  Available: "border-valid/30 bg-valid/10 text-valid",
  Preview: "border-brand/30 bg-brand/10 text-brand-soft",
  Pilot: "border-warn/30 bg-warn/10 text-warn",
  Planned: "border-line bg-panel-raised text-content-muted",
  Concept: "border-line bg-panel-raised text-content-muted",
};

export function ContinuousAssurance() {
  return (
    <div data-testid="continuous-assurance">
      <div className="flex flex-col gap-0 lg:flex-row lg:flex-wrap lg:items-stretch">
        {CONT.map((s, i) => (
          <React.Fragment key={s}>
            <motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={vp} transition={{ duration: 0.4, delay: i * 0.05, ease }} className="flex-1 rounded-lg border border-line bg-panel-raised/60 p-4">
              <span className="font-mono text-[9px] text-brand">{String(i + 1).padStart(2, "0")}</span>
              <div className="mt-1 text-[12.5px] leading-snug text-content">{s}</div>
            </motion.div>
            {i < CONT.length - 1 && <div className="flex items-center justify-center" aria-hidden><span className="my-1 h-4 w-px bg-brand/40 lg:my-0 lg:h-px lg:w-4" /></div>}
          </React.Fragment>
        ))}
      </div>
      <div className="mt-4 flex items-start gap-2 font-mono text-[10px] uppercase leading-relaxed tracking-[0.14em] text-brand"><span aria-hidden>↺</span><span>Loops back — assurance stays connected to the current AI system.</span></div>

      <div className="mt-8">
        <div className="mb-4 font-mono text-[10px] uppercase tracking-[0.2em] text-content-faint">Advanced capabilities · honest maturity</div>
        <div className="flex flex-wrap gap-2.5">
          {ADVANCED.map(([name, status]) => (
            <span key={name} className="inline-flex items-center gap-2 rounded-lg border border-line bg-panel-raised/60 px-3 py-2">
              <span className="text-[12px] text-content">{name}</span>
              <span className={`rounded-full border px-2 py-0.5 font-mono text-[8px] uppercase tracking-[0.12em] ${MAT_STYLE[status]}`}>{status}</span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ============================================================= */
/*  Certificate showcase — evidence → certificate + explorer     */
/* ============================================================= */
const EVIDENCE_GROUPS = [
  ["Security assessment", ["Prompt injection", "Data leakage", "Jailbreak resistance"]],
  ["Governance", ["Risk mapping", "Controls", "Ownership"]],
  ["Framework screening", ["ISO/IEC 42001", "NIST AI RMF", "EU AI Act"]],
  ["Findings", ["0 critical", "0 high open", "2 medium · 3 resolved"]],
  ["Evidence", ["Assessment record", "Remediation", "Retest", "Timestamp"]],
];

const FACETS = {
  "Trust Score": ["Security coverage · 92", "Governance coverage · 84", "Framework readiness · 78", "Evidence freshness · current", "Open findings · 2"],
  "Test Coverage": ["Prompt injection · pass", "Sensitive data leakage · pass", "Jailbreak resistance · pass", "Tool & permission abuse · review", "Output safety · pass"],
  "Standards Screened": ["ISO/IEC 42001", "NIST AI RMF", "OWASP LLM Top 10", "EU AI Act"],
  "Verified": ["ID · CERT-DEMO-000148", "Issued · 14 Aug 2026", "Valid until · 14 Aug 2027", "Status · Verified (current)"],
};
const FACET_KEYS = Object.keys(FACETS);
const SUPPORT_LINEAGE = ["Certificate", "Trust Passport", "Assurance", "Evidence", "Assessment · Finding · Risk · Control · Remediation · Retest · Framework"];

export function CertificateShowcase({ cert }) {
  const [facet, setFacet] = useState("Trust Score");
  const [explore, setExplore] = useState(false);
  const [cover, setCover] = useState(false);
  return (
    <div data-testid="certificate-showcase">
      <div className="grid gap-8 lg:grid-cols-[1fr_1.05fr] lg:items-start">
        {/* evidence converging */}
        <div>
          <div className="mb-4 font-mono text-[10px] uppercase tracking-[0.2em] text-content-faint">Supporting evidence</div>
          <div className="flex flex-col gap-3">
            {EVIDENCE_GROUPS.map(([title, items], i) => (
              <motion.div key={title} initial={{ opacity: 0, x: -16 }} whileInView={{ opacity: 1, x: 0 }} viewport={vp} transition={{ duration: 0.45, delay: i * 0.08, ease }} className="rounded-lg border border-line bg-panel-raised/60 p-4">
                <div className="font-mono text-[9px] uppercase tracking-[0.16em] text-brand-soft">{title}</div>
                <div className="mt-2 flex flex-wrap gap-1.5">{items.map((it) => <span key={it} className="rounded-full border border-line bg-panel-raised/60 px-2.5 py-0.5 font-mono text-[10px] tracking-wide text-content-muted">{it}</span>)}</div>
              </motion.div>
            ))}
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg border border-gold/25 bg-gold/[0.05] px-4 py-3">
            <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-gold">Converges into Meira assurance</span>
            <span className="text-content-faint">→</span>
            <span className="text-[12px] text-content-muted">Verifiable assurance certificate</span>
          </div>
          <p className="mt-3 text-[12px] leading-relaxed text-content-muted">The certificate is not the claim — the evidence is what supports it.</p>
        </div>

        {/* certificate + explorer */}
        <div className="lg:sticky lg:top-24">
          <Certificate cert={cert} testId="showcase-certificate" />

          {/* facet explorer */}
          <div className="mt-5 rounded-lg border border-line bg-panel-raised/60 p-4">
            <div className="flex flex-wrap gap-2">
              {FACET_KEYS.map((k) => (
                <button key={k} data-testid={`facet-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => setFacet(k)} className={`min-h-[36px] rounded-full border px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.1em] transition-colors ${facet === k ? "border-brand/40 bg-brand/10 text-brand" : "border-line text-content-muted hover:text-content"}`}>{k}</button>
              ))}
            </div>
            <motion.ul key={facet} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="mt-4 grid gap-2 sm:grid-cols-2">
              {FACETS[facet].map((v) => (
                <li key={v} className="flex items-center gap-2 text-[12.5px] text-content-muted"><Dot tone={C.gold} size={5} /><span>{v}</span></li>
              ))}
            </motion.ul>
            <p className="mt-3 font-mono text-[9px] leading-relaxed tracking-wide text-content-faint">Demonstration score shown for interface illustration. Actual scoring depends on configured assessment criteria and available evidence.</p>
          </div>

          {/* explore lineage */}
          <button data-testid="explore-support-btn" onClick={() => setExplore((v) => !v)} className="mt-3 inline-flex min-h-[44px] items-center gap-2 font-mono text-[11px] uppercase tracking-[0.12em] text-brand-soft transition-colors hover:text-content">
            <ChevronDown size={14} className={`transition-transform ${explore ? "rotate-180" : ""}`} /> Explore what supports this record
          </button>
          {explore && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="mt-3 overflow-hidden">
              <ol className="relative pl-6">
                <span className="absolute left-[5px] top-1 h-[calc(100%-1rem)] w-px bg-gradient-to-b from-gold/50 to-brand/40" />
                {SUPPORT_LINEAGE.map((s) => (
                  <li key={s} className="relative pb-3"><span className="absolute -left-6 top-1 h-2.5 w-2.5 rounded-full bg-brand" /><span className="text-[12.5px] text-content">{s}</span></li>
                ))}
              </ol>
            </motion.div>
          )}

          {/* what does this cover */}
          <button data-testid="cover-toggle-btn" onClick={() => setCover((v) => !v)} className="mt-2 inline-flex w-full items-center justify-between rounded-lg border border-line bg-panel-raised/60 px-4 py-3 text-left">
            <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-content">What does this certificate cover?</span>
            <ChevronDown size={16} className={`text-content-faint transition-transform ${cover ? "rotate-180" : ""}`} />
          </button>
          {cover && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="overflow-hidden">
              <ul className="mt-2 flex flex-col gap-2 rounded-lg border border-line bg-panel-raised/60 p-4 text-[12.5px] text-content-muted">
                {[
                  "AI system assessed · Customer Service Agent (demo)",
                  "Assessment type · AI Security Assessment + Governance Review",
                  "Testing categories · prompt injection, data leakage, jailbreak, tool abuse",
                  "Framework screening · ISO/IEC 42001, NIST AI RMF, EU AI Act, OWASP LLM",
                  "Findings summary · 0 critical, 0 high open, 2 medium (3 resolved)",
                  "Evidence timestamp · 14 Aug 2026 · Issue date · 14 Aug 2026",
                  "Current verification status · Verified (current)",
                  "Limitations · scope-bound; not a guarantee of universal or permanent safety",
                ].map((x) => <li key={x} className="flex items-start gap-2"><Dot tone={C.deep} size={5} /><span>{x}</span></li>)}
              </ul>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============================================================= */
/*  Public verification demo (sequence)                          */
/* ============================================================= */
const VSTEPS = ["CERT-DEMO-000148", "Check record", "Scope confirmed", "Status current", "Verified"];

export function VerificationDemo() {
  const [run, setRun] = useState(0);
  return (
    <div className="grid gap-8 lg:grid-cols-[1fr_1fr] lg:items-center" data-testid="verification-demo">
      <div>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className="h-[84px] w-[84px] shrink-0 rounded-md border border-gold/25 bg-qr-light p-2 sm:h-[92px] sm:w-[92px]">
            <VerifyQRWrap seed="a7f3c9e21b4d88f6e0c5a91d7b2e4f80c6a1d93e" />
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-content-faint">Scan or enter ID</div>
            <div className="mt-1 font-mono text-sm tracking-wider text-gold">CERT-DEMO-000148</div>
            <button data-testid="verification-run-btn" onClick={() => setRun((v) => v + 1)} className="mt-3 inline-flex min-h-[44px] items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.12em] text-ink transition-colors hover:bg-brand-soft">
              Check record
            </button>
          </div>
        </div>
        <p className="mt-4 max-w-sm text-[12px] leading-relaxed text-content-muted">Anyone with the certificate ID or QR code can check the record's authenticity and current status — no Meira account required.</p>
      </div>

      <ol className="flex flex-col gap-2.5">
        {VSTEPS.map((s, i) => (
          <motion.li key={`${run}-${s}`} initial={{ opacity: 0.25, x: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4, delay: run ? i * 0.5 : 0 }} className="flex items-center gap-3 rounded-lg border border-line bg-panel-raised/60 px-4 py-3">
            <span className="flex h-6 w-6 items-center justify-center rounded-full border border-line font-mono text-[10px] text-content-muted">{i + 1}</span>
            <span className={`text-[13px] ${i === VSTEPS.length - 1 ? "text-valid" : "text-content"}`}>{s}</span>
            {i === VSTEPS.length - 1 && <span className="ml-auto font-mono text-[9px] uppercase tracking-[0.14em] text-valid">✓ current</span>}
          </motion.li>
        ))}
      </ol>
    </div>
  );
}

/* small wrapper reusing the certificate QR look */
function VerifyQRWrap({ seed }) {
  const size = 21;
  const bits = [];
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  for (let i = 0; i < size * size; i++) { h = (h * 1103515245 + 12345) >>> 0; bits.push((h >> 16) & 1); }
  const finder = (r, c) => (r < 7 && c < 7) || (r < 7 && c >= size - 7) || (r >= size - 7 && c < 7);
  const fm = (r, c) => { const fr = r < 7 ? r : r - (size - 7); const fc = c < 7 ? c : c - (size - 7); return fr === 0 || fr === 6 || fc === 0 || fc === 6 || (fr >= 2 && fr <= 4 && fc >= 2 && fc <= 4); };
  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="h-full w-full" aria-hidden shapeRendering="crispEdges">
      {Array.from({ length: size }).map((_, r) => Array.from({ length: size }).map((__, c) => ((finder(r, c) ? fm(r, c) : bits[r * size + c] === 1) ? <rect key={`${r}-${c}`} x={c} y={r} width="1" height="1" fill={color("qr-dark")} /> : null)))}
    </svg>
  );
}
